import HelpIcon from "./HelpIcon";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Layout,
  Text,
  TextField,
  Tooltip,
} from './admin-ui';

import { c_cod_blacklist, d_cod_blacklist, q_cod_blacklists, u_cod_whitelist } from "../api";

const BLACKLIST_TYPE_IP = 1;
const BLACKLIST_TYPE_EMAIL = 3;
const BLACKLIST_TYPE_EMAIL_DOMAIN = 4;

const messageFromError = (error, fallback) => {
  const body = error?.response?.data || {};
  const errors = body.errors || body.data?.errors;
  const first = errors ? Object.values(errors).flat()[0] : null;
  return first || body.msg || body.message || fallback;
};

const RuleHeading = ({ title, description, main = false }) => (
  <InlineStack gap="150" blockAlign="center">
    <Text as={main ? "h2" : "h3"} variant={main ? "headingMd" : "headingSm"}>{title}</Text>
    <Tooltip content={description} preferredPosition="above" width="wide">
      <button
        type="button"
        aria-label={`About ${title}`}
        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, padding: 0, border: 0, borderRadius: "50%", background: "transparent", color: "#616161", cursor: "help" }}
      >
        <HelpIcon />
      </button>
    </Tooltip>
  </InlineStack>
);

const PolicyRow = ({ signal, action, tone, description }) => (
  <InlineStack align="space-between" blockAlign="center" gap="300" wrap={false}>
    <BlockStack gap="050">
      <Text as="p" fontWeight="semibold">{signal}</Text>
      <Text as="p" tone="subdued">{description}</Text>
    </BlockStack>
    <Badge tone={tone}>{action}</Badge>
  </InlineStack>
);

const BlacklistRules = ({ entries, emptyText, removingId, onRemove, disabled = false, removeLabel }) => {
  if (entries.length === 0) {
    return <Text as="p" tone="subdued">{emptyText}</Text>;
  }

  return (
    <BlockStack gap="200">
      {entries.map((entry) => (
        <Card key={entry.id} background="bg-surface-secondary">
          <InlineStack align="space-between" blockAlign="center" gap="300" wrap={false}>
            <BlockStack gap="100">
              <InlineStack gap="200" blockAlign="center">
                <Badge>{Number(entry.subject_type) === BLACKLIST_TYPE_EMAIL_DOMAIN ? "Domain" : Number(entry.subject_type) === BLACKLIST_TYPE_IP ? "IP" : "Email"}</Badge>
                <Text as="p" fontWeight="semibold">{entry.subject_masked || "Hidden"}</Text>
              </InlineStack>
              {entry.reason ? <Text as="p" tone="subdued">{entry.reason}</Text> : null}
            </BlockStack>
            <Button
              variant="plain"
              tone="critical"
              onClick={() => onRemove(entry)}
              loading={removingId === entry.id}
              disabled={disabled}
              accessibilityLabel={removeLabel?.(entry)}
            >
              Remove
            </Button>
          </InlineStack>
        </Card>
      ))}
    </BlockStack>
  );
};

export default function CodRiskSettings({ settings, update, onSave, saving, loading, userToken }) {
  const [blacklists, setBlacklists] = useState([]);
  const [blacklistsLoading, setBlacklistsLoading] = useState(false);
  const [savingRule, setSavingRule] = useState("");
  const [removingId, setRemovingId] = useState(null);
  const [ruleNotice, setRuleNotice] = useState(null);
  const [ipValue, setIpValue] = useState("");
  const [ipReason, setIpReason] = useState("");
  const [whitelistValue, setWhitelistValue] = useState("");
  const [whitelistReason, setWhitelistReason] = useState("");
  const [whitelistSaving, setWhitelistSaving] = useState(false);
  const saveWhitelist = async (ip, remove = false) => {
    if (whitelistSaving) return;
    setWhitelistSaving(true);
    try {
      const response = await u_cod_whitelist({ user_token: userToken, ip: ip.trim(), reason: remove ? "" : whitelistReason.trim(), remove: remove ? 1 : 0 });
      update("ip_whitelist", response.data.ip_whitelist);
      update("ip_whitelist_entries", response.data.ip_whitelist_entries);
      if (!remove) {
        setWhitelistValue("");
        setWhitelistReason("");
      }
      setRuleNotice({ tone: "success", message: response.msg || "IP whitelist updated." });
    } catch (error) {
      setRuleNotice({ tone: "critical", message: messageFromError(error, "Unable to update IP whitelist.") });
    } finally {
      setWhitelistSaving(false);
    }
  };
  const [emailValue, setEmailValue] = useState("");
  const [emailReason, setEmailReason] = useState("");
  const ipRules = useMemo(() => blacklists.filter(entry => Number(entry.subject_type) === BLACKLIST_TYPE_IP), [blacklists]);
  const emailRules = useMemo(() => blacklists.filter(entry => [BLACKLIST_TYPE_EMAIL, BLACKLIST_TYPE_EMAIL_DOMAIN].includes(Number(entry.subject_type))), [blacklists]);


  const loadBlacklists = useCallback(async () => {
    if (!userToken) {
      setBlacklists([]);
      return;
    }
    try {
      setBlacklistsLoading(true);
      const response = await q_cod_blacklists({ user_token: userToken });
      setBlacklists(response.data?.list || []);
    } catch (error) {
      setRuleNotice({ tone: "critical", message: messageFromError(error, "Unable to load blacklist rules.") });
    } finally {
      setBlacklistsLoading(false);
    }
  }, [userToken]);

  useEffect(() => {
    loadBlacklists();
  }, [loadBlacklists]);

  const addRule = async (subject) => {
    const value = (subject === "ip" ? ipValue : emailValue).trim();
    if (!value) {
      setRuleNotice({ tone: "critical", message: subject === "ip" ? "Enter an IP address." : "Enter an email address or domain rule." });
      return;
    }

    try {
      setSavingRule(subject);
      setRuleNotice(null);
      const response = await c_cod_blacklist({
        user_token: userToken,
        subject,
        value,
        reason: subject === "ip" ? ipReason.trim() : emailReason.trim(),
      });
      if (subject === "ip") {
        setIpValue("");
        setIpReason("");
      } else {
        setEmailValue("");
        setEmailReason("");
      }
      setRuleNotice({ tone: "success", message: response.msg || "Blacklist rule added." });
      await loadBlacklists();
    } catch (error) {
      setRuleNotice({ tone: "critical", message: messageFromError(error, "Unable to add the blacklist rule.") });
    } finally {
      setSavingRule("");
    }
  };

  const removeRule = async (entry) => {
    try {
      setRemovingId(entry.id);
      setRuleNotice(null);
      const response = await d_cod_blacklist({ user_token: userToken, id: entry.id });
      setBlacklists((current) => current.filter((item) => item.id !== entry.id));
      setRuleNotice({ tone: "success", message: response.msg || "Blacklist rule removed." });
    } catch (error) {
      setRuleNotice({ tone: "critical", message: messageFromError(error, "Unable to remove the blacklist rule.") });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Layout>
      <Layout.Section>
        <BlockStack gap="400">
          <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
            <Card>
              <BlockStack gap="150">
                <Text as="p" tone="subdued">Order velocity</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p" variant="headingMd">Active</Text>
                  <Badge tone="success">Enforced</Badge>
                </InlineStack>
                <Text as="p" tone="subdued">Limits repeated COD attempts by IP and phone.</Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="150">
                <Text as="p" tone="subdued">Identity blacklist</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="p" variant="headingMd">Active</Text>
                  <Badge tone="success">Blocking</Badge>
                </InlineStack>
                <Text as="p" tone="subdued">Dashboard and Risk settings IP, phone, email, and domain rules are enforced.</Text>
              </BlockStack>
            </Card>
          </InlineGrid>

          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center" gap="300">
                <RuleHeading main title="COD blacklist rules" description="Manage trusted IP exceptions and block COD submissions by IP, email address, or email domain. Domain rules use the format *@gmail.com and match every email address under that domain. Rules affect COD orders only. Whitelisted IPs take priority over blocking rules." />
                <Badge tone="info">Whitelist first</Badge>
              </InlineStack>
              {ruleNotice ? (
                <Banner tone={ruleNotice.tone} onDismiss={() => setRuleNotice(null)}>{ruleNotice.message}</Banner>
              ) : null}
              <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
                <BlockStack gap="300">
                  <RuleHeading title="IP whitelist" description="Whitelisted IPs bypass all COD blacklist, IP/phone limits and TrafficArmor checks for this store. Product, address and amount validation still applies. Enter one exact IPv4 or IPv6 address; ranges are not supported. Additions and removals take effect immediately. You may include an optional reason." />
                  <TextField label="Whitelist IP address" placeholder="203.0.113.10" value={whitelistValue} onChange={setWhitelistValue} autoComplete="off" disabled={loading || saving || !userToken} />
                  <TextField label="Reason (optional)" placeholder="Trusted customer or testing IP" value={whitelistReason} onChange={setWhitelistReason} maxLength={500} autoComplete="off" disabled={loading || saving || whitelistSaving || !userToken} />
                  <InlineStack align="end">
                    <Button loading={whitelistSaving} disabled={loading || saving || !userToken || !whitelistValue.trim()} onClick={() => saveWhitelist(whitelistValue)}>Add to whitelist</Button>
                  </InlineStack>
                  <BlacklistRules
                    entries={(settings.ip_whitelist || []).map((ip) => ({ id: ip, subject_type: BLACKLIST_TYPE_IP, subject_masked: ip, reason: (settings.ip_whitelist_entries || []).find((entry) => entry.ip === ip)?.reason || "" }))}
                    emptyText="No IP whitelist rules."
                    disabled={loading || saving || whitelistSaving || !userToken}
                    removeLabel={(entry) => `Remove whitelist IP ${entry.id}`}
                    onRemove={(entry) => saveWhitelist(entry.id, true)}
                  />
                </BlockStack>
                <BlockStack gap="300">
                  <RuleHeading title="IP blacklist" description="Enter one IPv4 or IPv6 address per rule." />
                  <TextField
                    label="IP address"
                    value={ipValue}
                    onChange={setIpValue}
                    placeholder="203.0.113.10"
                    autoComplete="off"
                    disabled={loading || !userToken}
                  />
                  <TextField
                    label="Reason (optional)"
                    value={ipReason}
                    onChange={setIpReason}
                    placeholder="Repeated refused deliveries"
                    maxLength={500}
                    autoComplete="off"
                    disabled={loading || !userToken}
                  />
                  <InlineStack align="end">
                    <Button onClick={() => addRule("ip")} loading={savingRule === "ip"} disabled={loading || !userToken || savingRule !== ""}>
                      Add IP
                    </Button>
                  </InlineStack>
                  {blacklistsLoading ? (
                    <Text as="p" tone="subdued">Loading IP blacklist...</Text>
                  ) : (
                    <BlacklistRules entries={ipRules} emptyText="No active IP blacklist rules." removingId={removingId} onRemove={removeRule} />
                  )}
                </BlockStack>

                <BlockStack gap="300">
                  <RuleHeading title="Email blocking" description="Use an exact address or *@domain.com to block the whole domain." />
                  <TextField
                    label="Email address or domain"
                    value={emailValue}
                    onChange={setEmailValue}
                    placeholder="customer@example.com or *@gmail.com"
                    autoComplete="off"
                    disabled={loading || !userToken}
                  />
                  <TextField
                    label="Reason (optional)"
                    value={emailReason}
                    onChange={setEmailReason}
                    placeholder="Known high-risk email source"
                    maxLength={500}
                    autoComplete="off"
                    disabled={loading || !userToken}
                  />
                  <InlineStack align="end">
                    <Button onClick={() => addRule("email")} loading={savingRule === "email"} disabled={loading || !userToken || savingRule !== ""}>
                      Add email rule
                    </Button>
                  </InlineStack>
                  {blacklistsLoading ? (
                    <Text as="p" tone="subdued">Loading email rules...</Text>
                  ) : (
                    <BlacklistRules entries={emailRules} emptyText="No active email blacklist rules." removingId={removingId} onRemove={removeRule} />
                  )}
                </BlockStack>
              </InlineGrid>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">Order velocity limits</Text>
                <Text as="p" tone="subdued">
                  These controls are enforced before a Shopify COD order is accepted. Existing blacklist rules remain hard blocks.
                </Text>
              </BlockStack>
              <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                <TextField
                  label="Maximum orders per IP per hour"
                  type="number"
                  min={1}
                  max={1000}
                  value={String(settings.max_orders_per_ip_hour)}
                  onChange={(value) => update("max_orders_per_ip_hour", value)}
                  helpText="Use a higher value for mobile-heavy traffic because carrier networks share public IPs."
                  autoComplete="off"
                  disabled={loading}
                />
                <TextField
                  label="Maximum orders per phone per day"
                  type="number"
                  min={1}
                  max={1000}
                  value={String(settings.max_orders_per_phone_day)}
                  onChange={(value) => update("max_orders_per_phone_day", value)}
                  helpText="Phone velocity is a stronger customer signal than IP velocity."
                  autoComplete="off"
                  disabled={loading}
                />
              </InlineGrid>
            </BlockStack>
          </Card>

          <Card>
            <InlineGrid columns={{ xs: 1, sm: "1fr auto" }} gap="400" alignItems="center">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">Advanced blocking</Text>
                <Text as="p" tone="subdued">Use TrafficArmor to block selected IP types and apply campaign-based checks to COD orders. Manage these settings in Traffic protection.</Text>
              </BlockStack>
              <Button url={'/integrations/traffic-protection' + window.location.search}>Open TrafficArmor</Button>
            </InlineGrid>
          </Card>

          <InlineStack align="end"><Button variant="primary" onClick={onSave} loading={saving} disabled={loading || !userToken}>Save risk settings</Button></InlineStack>

          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center" gap="300">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">COD decision behavior</Text>
                  <Text as="p" tone="subdued">Risk checks run in a fixed order before Shopify receives the COD order.</Text>
                </BlockStack>
                <Badge tone="info">COD only</Badge>
              </InlineStack>
              <BlockStack gap="300">
                <PolicyRow signal="0. IP whitelist matches" action="Skip risk checks" tone="success" description="Takes priority over every COD risk block, including customer blacklists and phone limits." />
                <PolicyRow signal="1. IP or email blacklist matches" action="Block COD" tone="critical" description="For non-whitelisted IPs, manual IP, exact email and email-domain rules are hard blocks." />
                <PolicyRow signal="2. Local velocity limit is exceeded" action="Block COD" tone="critical" description="Atomic IP-hour and phone-day capacity checks run before external services." />
              </BlockStack>
              <Text as="p" tone="subdued">
                Orders that pass every hard block are created normally.
              </Text>
            </BlockStack>
          </Card>
        </BlockStack>
      </Layout.Section>
    </Layout>
  );
}
